import { useState, useCallback, useContext, useMemo, useRef, useEffect } from 'react';
import { trackEvent } from 'services/userEvent.service';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Star, ShoppingCart, Heart, Play, Minus, Plus, Send, Check, Banknote } from 'lucide-react';
import classNames from 'classnames';
import Link from 'next/link';
import Image from 'components/core/Image';
import Loader from 'components/common/Loader';
import { AddedToCartContext } from 'contexts/addedToCartContext';
import { CartContext } from 'contexts/cartContext';
import { AppContext } from 'contexts';
import { CART_ACTIONS_TYPES } from 'constants/cart';
import { CURRENCY, OutsideHookTypes } from 'constants/';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import useReviews from 'hooks/api/useReviews';
import useOutside from 'hooks/common/useOutside';

import styles from './index.module.scss';

type Tab = 'related' | 'description' | 'specifications' | 'reviews';

const Card = ({ related, product }: { product: Product.Item; related: Product.Item[] }) => {
  const { updateItem, cart } = useContext(CartContext);
  const { currency } = useContext(AppContext);
  const { notify } = useContext(AddedToCartContext);
  const [amount, setAmount] = useState(1);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(related?.length > 0 ? 'related' : 'description');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewName, setReviewName] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { push, locale } = useRouter();
  const { t } = useTranslation('card');

  const [reviews, setReviews] = useState<IReview[]>(product?.reviews ?? []);
  useEffect(() => { setReviews(product?.reviews ?? []); }, [product?.link]);
  const { submitReview, submitting } = useReviews(product?.link);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const tabsRef = useRef<HTMLElement>(null);
  const shareRef = useRef<HTMLInputElement>(null);

  useEffect(() => { trackEvent('productPreview'); }, []);

  const closeShare = useCallback(() => setShareOpen(false), []);
  useOutside({ ref: shareRef, wiretapState: shareOpen, type: OutsideHookTypes.SEARCH, func: closeShare });

  const media = product?.assets?.items ?? [];
  const selected = media[selectedIdx];

  const productName = useMemo<string>(() => {
    if (locale && product?.valueTranslation?.[locale]?.name) return product.valueTranslation[locale].name;
    return product?.name ?? '';
  }, [locale, product]);

  const categoryName = useMemo<string>(() => {
    const category = product?.category as unknown as Category & { translation?: { language: string; name: string }[] };
    const translated = Array.isArray(category?.translation)
      ? category.translation.find((tr) => tr.language === locale)?.name
      : undefined;
    return translated || category?.name || '';
  }, [locale, product]);

  const description = useMemo<string>(() => {
    const promDescription = product?.promDescription?.[locale === 'ru' ? 'ru' : 'ua'];
    if (promDescription) return promDescription;
    if (!product?.valueDescription || !locale) return '';
    if (locale && product.valueTranslation?.[locale]) return product.valueTranslation[locale]?.description ?? '';
    return product.valueDescription;
  }, [locale, product]);

  const cartLabel = useMemo(() => {
    const itemPrice = product?.discountPrice || product?.price?.value;
    return `${CURRENCY[currency].sign}${itemPrice} ${productName} (x${amount})`;
  }, [product, currency, productName, amount]);

  const isInCart = useMemo(
    () => cart.some((i) => `${i.originPrice}${i.id}` === `${product?.originPrice}${product?.id}`),
    [cart, product],
  );

  const addToCart = useCallback(() => {
    if (isInCart) {
      void push('/checkout');
      return;
    }
    updateItem(product, amount, CART_ACTIONS_TYPES.UPDATE);
    trackEvent('addToCard', cartLabel);
    notify({ name: productName, image: product?.assets?.items?.find((m) => m.type === 'img')?.src });
  }, [isInCart, push, updateItem, product, amount, cartLabel, notify, productName]);

  const handleCopyLink = useCallback(() => {
    void navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const handleStarClick = useCallback((starIndex: number) => {
    setActiveTab('reviews');
    setReviewRating(starIndex + 1);
    const timeout = setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      clearTimeout(timeout);
    }, 50);
  }, []);

  const handleReviewSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const newReview = { name: reviewName, stars: reviewRating, description: reviewComment, date: new Date().toISOString() };
    submitReview(
      { name: reviewName, stars: reviewRating, description: reviewComment },
      {
        onSuccess: () => {
          setReviews((prev) => [...prev, newReview]);
          setReviewName('');
          setReviewComment('');
          setReviewRating(5);
          setSubmitted(true);
          setTimeout(() => setSubmitted(false), 4000);
        },
      },
    );
  }, [submitReview, reviewName, reviewRating, reviewComment]);

  const tabs: { key: Tab; label: string }[] = [
    ...(related?.length > 0 ? [{ key: 'related' as Tab, label: t('related') }] : []),
    { key: 'description', label: t('tabDescription') },
    { key: 'specifications', label: t('tabSpecifications') },
    { key: 'reviews', label: t('tabReviews') },
  ];

  if (!product) return <div className={styles.loaderWrap}><Loader /></div>;

  return (
    <div className={styles.page}>
      <div className={styles.inner}>

        {/* ── Breadcrumb ── */}
        <nav className={styles.breadcrumb}>
          <Link href='/' className={styles.breadcrumbLink}>{locale === 'ru' ? 'Главная' : 'Головна'}</Link>
          <ChevronRight size={14} className={styles.breadcrumbSep} />
          <Link href={`/category/${product.category.link}`} className={styles.breadcrumbLink}>
            {categoryName}
          </Link>
          <ChevronRight size={14} className={styles.breadcrumbSep} />
          <span className={styles.breadcrumbCurrent}>{productName}</span>
        </nav>

        {/* ── Main grid ── */}
        <div className={styles.grid}>

          {/* ── Left: gallery ── */}
          <motion.div
            className={styles.gallery}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className={styles.mainMediaWrap}>
              <AnimatePresence mode='wait'>
                <motion.div
                  key={selectedIdx}
                  className={styles.mainMedia}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {selected?.type === 'video' ? (
                    isVideoPlaying ? (
                      <video
                        src={selected.src}
                        controls
                        autoPlay
                        className={styles.video}
                        onEnded={() => setIsVideoPlaying(false)}
                      />
                    ) : (
                      <div className={styles.videoThumb} onClick={() => setIsVideoPlaying(true)}>
                        <Image
                          src={media.find((m) => m.type === 'img')?.src || ''}
                          alt={productName}
                          width={1200}
                          height={1200}
                          quality={90}
                          sizes='(max-width: 1024px) 100vw, 50vw'
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <div className={styles.playOverlay}>
                          <motion.div className={styles.playBtn} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <Play size={36} className={styles.playIcon} />
                          </motion.div>
                        </div>
                      </div>
                    )
                  ) : (
                    <Image
                      src={selected?.src || media.find((m) => m.type === 'img')?.src || ''}
                      alt={productName}
                      width={1200}
                      height={1200}
                      quality={90}
                      priority
                      sizes='(max-width: 1024px) 100vw, 50vw'
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className={styles.thumbs}>
              {media.map((m, i) => (
                <motion.div
                  key={i}
                  className={`${styles.thumb} ${selectedIdx === i ? styles.thumbActive : ''}`}
                  whileHover={{ scale: 1.05 }}
                  onHoverStart={() => {
                    if (m.type === 'img') {
                      setSelectedIdx(i);
                      setIsVideoPlaying(false);
                    }
                  }}
                  onClick={() => {
                    setSelectedIdx(i);
                    setIsVideoPlaying(m.type === 'video');
                  }}
                >
                  <Image
                    loader={() => (m.type === 'img' ? m.src : (media.find((x) => x.type === 'img')?.src || ''))}
                    src={m.type === 'img' ? m.src : (media.find((x) => x.type === 'img')?.src || '')}
                    alt={`${productName} ${i + 1}`}
                    width={120}
                    height={120}
                    unoptimized
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {m.type === 'video' && (
                    <div className={styles.thumbPlayOverlay}>
                      <Play size={16} className={styles.playIcon} />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── Right: info ── */}
          <motion.div
            className={styles.info}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
          >
            {product?.age && (
            <span className={styles.ageBadge}>
              {t('ages')}
              {' '}
              {product.age}
            </span>
            )}

            <h1 className={styles.title}>{productName}</h1>

            <div className={styles.starsRow}>
              <div className={styles.stars}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <button
                    key={i}
                    type='button'
                    onClick={() => handleStarClick(i)}
                    className={styles.starBtn}
                  >
                    <Star
                      size={18}
                      className={i < Math.round(reviews.reduce((s, r) => s + r.stars, 0) / (reviews.length || 1))
                        ? styles.starFilled : styles.starEmpty}
                    />
                  </button>
                ))}
              </div>
              <span className={styles.ratingText}>
                {reviews.length > 0
                  ? `${(reviews.reduce((s, r) => s + r.stars, 0) / reviews.length).toFixed(1)} (${reviews.length} ${t('tabReviews').toLowerCase()})`
                  : t('noReviews')}
              </span>
            </div>

            <div className={styles.priceRow}>
              <span className={styles.price}>
                {CURRENCY[currency].sign}
                {product.discountPrice || product.price.value}
              </span>
              {product.discountPrice && (
                <span className={styles.oldPrice}>
                  {CURRENCY[currency].sign}
                  {product.price.value}
                </span>
              )}
              {product.discount > 0 && (
                <span className={styles.discountBadge}>
                  -
                  {product.discount}
                  %
                </span>
              )}
            </div>

            {!product.outOfStock && (
              <div className={styles.actions}>
                <div className={styles.quantityRow}>
                  <div className={styles.qtyControl}>
                    <button
                      type='button'
                      className={styles.qtyBtn}
                      onClick={() => setAmount((a) => Math.max(1, a - 1))}
                    >
                      <Minus size={16} />
                    </button>
                    <span className={styles.qtyValue}>{amount}</span>
                    <button
                      type='button'
                      className={styles.qtyBtn}
                      onClick={() => setAmount((a) => a + 1)}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                <div className={styles.btnRow}>
                  <motion.button
                    type='button'
                    className={classNames(styles.cartBtn, { [styles.cartBtnInCart]: isInCart })}
                    onClick={addToCart}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {isInCart ? <Check size={20} /> : <ShoppingCart size={20} />}
                    {isInCart ? t('inCartBtn') : t('addToCardBtn')}
                  </motion.button>
                  <div ref={shareRef} className={styles.shareWrap}>
                    <motion.button
                      type='button'
                      className={styles.wishBtn}
                      onClick={() => setShareOpen((v) => !v)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Heart size={20} />
                    </motion.button>

                    <AnimatePresence>
                      {shareOpen && (
                        <motion.div
                          className={styles.sharePopup}
                          initial={{ opacity: 0, scale: 0.9, y: 6 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9, y: 6 }}
                          transition={{ duration: 0.15 }}
                        >
                          <p className={styles.shareTitle}>{t('shareTitle')}</p>
                          <div className={styles.shareGrid}>
                            <a
                              href={`https://t.me/share/url?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}&text=${encodeURIComponent(productName)}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className={`${styles.shareBtn} ${styles.shareTelegram}`}
                            >
                              <svg width='20' height='20' viewBox='0 0 24 24' fill='currentColor'><path d='M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L8.32 13.617l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.828.942z' /></svg>
                              Telegram
                            </a>
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(`${productName} ${typeof window !== 'undefined' ? window.location.href : ''}`)}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className={`${styles.shareBtn} ${styles.shareWhatsapp}`}
                            >
                              <svg width='20' height='20' viewBox='0 0 24 24' fill='currentColor'><path d='M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.38 1.26 4.78L2.05 22l5.51-1.32c1.34.73 2.87 1.15 4.48 1.15 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.52 14.16c-.23.63-1.35 1.21-1.86 1.27-.48.06-1.08.08-1.74-.11-.4-.12-.91-.28-1.56-.55-2.74-1.19-4.53-3.96-4.67-4.14-.14-.18-1.13-1.5-1.13-2.87 0-1.36.71-2.03 1-2.34.26-.27.58-.34.77-.34l.55.01c.18 0 .42-.07.66.5.25.6.84 2.06.91 2.21.07.15.12.32.02.51-.09.19-.14.31-.27.47l-.41.48c-.14.14-.28.29-.12.56.16.27.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.21 1.36.27.14.43.12.59-.07.16-.19.68-.79.86-1.06.18-.27.36-.22.61-.13.24.09 1.56.74 1.83.87.27.14.45.21.51.32.07.11.07.63-.16 1.26z' /></svg>
                              WhatsApp
                            </a>
                            <a
                              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className={`${styles.shareBtn} ${styles.shareFacebook}`}
                            >
                              <svg width='20' height='20' viewBox='0 0 24 24' fill='currentColor'><path d='M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.791-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.885v2.269h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z' /></svg>
                              Facebook
                            </a>
                            <a
                              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}&text=${encodeURIComponent(productName)}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className={`${styles.shareBtn} ${styles.shareX}`}
                            >
                              <svg width='20' height='20' viewBox='0 0 24 24' fill='currentColor'><path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.912-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z' /></svg>
                              X
                            </a>
                          </div>
                          <button
                            type='button'
                            className={styles.shareCopy}
                            onClick={handleCopyLink}
                          >
                            {copied ? `✓ ${t('shareCopied')}` : `🔗 ${t('shareCopyLink')}`}
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <p className={styles.shippingNote}>
                  {t('exchangeReturn14')}
                  {', '}
                  {t('dailyShipping').toLowerCase()}
                </p>

              </div>
            )}

            {product.outOfStock && (
              <div className={styles.outOfStockBadge}>{t('outOfStock')}</div>
            )}

            <div className={styles.features}>
              <div className={styles.featureCard}>
                <div className={styles.featureIcon} style={{ background: '#fff3f3' }}>
                  <Image
                    src='/nova-post.png'
                    loader={() => '/nova-post.png'}
                    alt='Нова пошта'
                    width={36}
                    height={36}
                    style={{ objectFit: 'contain' }}
                  />
                </div>
                <div className={styles.featureBody}>
                  <p className={styles.featureTitle} style={{ color: '#d62b2b' }}>{t('pickupTitle')}</p>
                  <p className={styles.featureSub}>{t('pickupDelivery')}</p>
                  <p className={styles.featureSub}>{t('pickupTariff')}</p>
                </div>
              </div>
              <div className={styles.featureCard}>
                <div className={styles.featureIcon} style={{ background: '#f5f0ff' }}>
                  <Image
                    src='/mono-removebg-preview.png'
                    loader={() => '/mono-removebg-preview.png'}
                    alt='Monobank'
                    width={36}
                    height={36}
                    style={{ objectFit: 'contain', borderRadius: 8 }}
                  />
                </div>
                <div className={styles.featureBody}>
                  <p className={styles.featureTitle}>{t('monoTitle')}</p>
                  <p className={styles.featureSub}>{t('monoSub')}</p>
                </div>
              </div>
              <div className={styles.featureCard}>
                <div className={styles.featureIcon} style={{ background: '#eefbf1' }}>
                  <Banknote size={22} color='#1a9e52' />
                </div>
                <div className={styles.featureBody}>
                  <p className={styles.featureTitle}>{t('codTitle')}</p>
                  <p className={styles.featureSub}>{t('codSub')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Tabs: Description / Specifications / Reviews ── */}
        <section ref={tabsRef} className={styles.tabsSection}>
          <div className={styles.tabBar}>
            {tabs.map(({ key, label }) => (
              <button
                key={key}
                type='button'
                className={`${styles.tabBtn} ${activeTab === key ? styles.tabBtnActive : ''}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
                {key === 'reviews' && reviews.length > 0 && (
                  <span className={styles.tabCount}>{reviews.length}</span>
                )}
                {activeTab === key && (
                  <motion.div className={styles.tabUnderline} layoutId='tabUnderline' />
                )}
              </button>
            ))}
          </div>

          <div className={styles.tabContent}>
            {related?.length > 0 && (
              <div
                className={classNames(styles.relatedList, { [styles.tabPanelHidden]: activeTab !== 'related' })}
              >
                {related.map((item) => (
                  <Link key={item.link} href={`/product/${item.link}`}>
                    <motion.div className={styles.relatedListItem} whileHover={{ x: 4 }} transition={{ duration: 0.15 }}>
                      <div className={styles.relatedListImg}>
                        <Image
                          loader={() => item.assets.items.find((a) => a.type === 'img')?.src || ''}
                          src={item.assets.items.find((a) => a.type === 'img')?.src || ''}
                          alt={item.name}
                          width={80}
                          height={80}
                          unoptimized
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {item.discount > 0 && (
                          <span className={styles.relatedBadge}>
                            -
                            {item.discount}
                            %
                          </span>
                        )}
                      </div>
                      <div className={styles.relatedListBody}>
                        <p className={styles.relatedListName}>
                          {(locale && item.valueTranslation?.[locale]?.name) ? item.valueTranslation[locale].name : item.name}
                        </p>
                        <span className={styles.relatedListPrice}>
                          {CURRENCY[currency].sign}
                          {item.discountPrice || item.price.value}
                        </span>
                      </div>
                      <ChevronRight size={18} className={styles.relatedListArrow} />
                    </motion.div>
                  </Link>
                ))}
              </div>
            )}

            <div className={classNames({ [styles.tabPanelHidden]: activeTab !== 'description' })}>
              {description ? (
                <div className={styles.descContent} dangerouslySetInnerHTML={{ __html: description }} />
              ) : (
                <p className={styles.emptyMsg}>{t('noSpecifications')}</p>
              )}
            </div>

            <div className={classNames({ [styles.tabPanelHidden]: activeTab !== 'specifications' })}>
              {product.characteristics && product.characteristics.length > 0 ? (
                <div className={styles.specsTable}>
                  {product.characteristics.map((c, i) => (
                    <div key={i} className={`${styles.specsRow} ${i % 2 === 0 ? styles.specsRowEven : ''}`}>
                      <span className={styles.specsName}>{c.name}</span>
                      <span className={styles.specsValue}>{c.value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyMsg}>{t('noSpecifications')}</p>
              )}
            </div>

            <div className={classNames(styles.reviewsWrap, { [styles.tabPanelHidden]: activeTab !== 'reviews' })}>
              {/* Reviews list */}
              <div className={styles.reviewsList}>
                <h3 className={styles.reviewsSubtitle}>{t('customerReviews')}</h3>
                {reviews.length === 0 && (
                  <p className={styles.emptyMsg}>{t('noReviews')}</p>
                )}
                {reviews.map((review, i) => (
                  <motion.div
                    key={i}
                    className={styles.reviewCard}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <div className={styles.reviewHeader}>
                      <div>
                        <p className={styles.reviewAuthor}>{review.name}</p>
                        <p className={styles.reviewDate}>
                          {new Date(review.date).toLocaleDateString(locale === 'ru' ? 'ru-UA' : 'uk-UA', {
                            year: 'numeric', month: 'long', day: 'numeric',
                          })}
                        </p>
                      </div>
                      <div className={styles.reviewStars}>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            size={16}
                            className={i < review.stars ? styles.starFilled : styles.starEmpty}
                          />
                        ))}
                      </div>
                    </div>
                    <p className={styles.reviewText}>{review.description}</p>
                  </motion.div>
                ))}
              </div>

              {/* Review form */}
              <div className={styles.reviewForm}>
                <h3 className={styles.reviewsSubtitle}>{t('writeReview')}</h3>
                <form onSubmit={handleReviewSubmit} className={styles.formInner}>
                  <div className={styles.formField}>
                    <label className={styles.formLabel}>{t('yourRating')}</label>
                    <div className={styles.starPicker}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <motion.button
                          key={i}
                          type='button'
                          onClick={() => setReviewRating(i + 1)}
                          whileHover={{ scale: 1.2 }}
                          whileTap={{ scale: 0.9 }}
                          className={styles.starPickerBtn}
                        >
                          <Star
                            size={28}
                            className={i < reviewRating ? styles.starFilled : styles.starPickerEmpty}
                          />
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  <div className={styles.formField}>
                    <label className={styles.formLabel}>{t('yourName')}</label>
                    <input
                      type='text'
                      value={reviewName}
                      onChange={(e) => setReviewName(e.target.value)}
                      placeholder={t('namePlaceholder')}
                      required
                      className={styles.formInput}
                    />
                  </div>

                  <div className={styles.formField}>
                    <label className={styles.formLabel}>{t('yourReview')}</label>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder={t('reviewPlaceholder')}
                      required
                      rows={4}
                      className={styles.formTextarea}
                    />
                  </div>

                  {submitted && (
                    <motion.p
                      className={styles.successMsg}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {t('reviewSuccess')}
                    </motion.p>
                  )}

                  <motion.button
                    type='submit'
                    className={styles.submitBtn}
                    disabled={submitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {submitting ? <Loader /> : (
                      <>
                        <Send size={18} />
                        {t('submitReview')}
                      </>
                    )}
                  </motion.button>
                </form>
              </div>
            </div>
          </div>
        </section>

      </div>

      {!product.outOfStock && (
        <div className={styles.mobileBuyBar}>
          <div className={styles.mobileBuyPrice}>
            <span className={styles.mobileBuyPriceValue}>
              {CURRENCY[currency].sign}
              {product.discountPrice || product.price.value}
            </span>
            {product.discountPrice && (
              <span className={styles.mobileBuyOldPrice}>
                {CURRENCY[currency].sign}
                {product.price.value}
              </span>
            )}
          </div>
          <motion.button
            type='button'
            className={classNames(styles.mobileBuyBtn, { [styles.mobileBuyBtnInCart]: isInCart })}
            onClick={addToCart}
            whileTap={{ scale: 0.97 }}
          >
            {isInCart ? <Check size={18} /> : <ShoppingCart size={18} />}
            {isInCart ? t('inCartBtn') : t('addToCardBtn')}
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default Card;
